import {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';

import { asyncScriptLoad } from './Recaptcha.helpers';
import { YANDEX_CAPTCHA_SCRIPT_URL, YANDEX_CAPTCHA_SITE_KEY } from './Recaptcha.constants';
import { RecaptchaExposedApi } from './Recaptcha.types';

type PendingToken = {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
};

// Invisible SmartCaptcha answers through callbacks, not a promise: the pending
// submit is parked in a ref and settled by whichever widget event comes first.
export function useYandexCaptcha(isEnabled: boolean): RecaptchaExposedApi {
  const [recaptchaErrorCode, setRecaptchaErrorCode] = useState(''); // RIEXXX - Recaptcha Internal Error
  const widgetIdRef = useRef<number | null>(null);
  const pendingRef = useRef<PendingToken | null>(null);

  const settle = useCallback((token: string | null, error?: Error): void => {
    const pending = pendingRef.current;
    pendingRef.current = null;

    if (!pending) {
      return;
    }

    if (token) {
      pending.resolve(token);
    } else {
      pending.reject(error ?? new Error('SmartCaptcha is not passed'));
    }
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      return undefined;
    }

    const container = document.createElement('div');
    document.body.appendChild(container);

    asyncScriptLoad(YANDEX_CAPTCHA_SCRIPT_URL)
      .then(() => {
        const { smartCaptcha } = window as any;
        const widgetId = smartCaptcha.render(container, {
          sitekey: YANDEX_CAPTCHA_SITE_KEY,
          invisible: true,
          hl: 'ru',
          callback: (token: string) => settle(token),
        });

        // A closed challenge never calls back, and the submit would hang on it forever;
        // a solved one may close before the callback, so the token is read here too.
        smartCaptcha.subscribe(widgetId, 'challenge-hidden', () => settle(smartCaptcha.getResponse(widgetId) || null));
        smartCaptcha.subscribe(widgetId, 'network-error', () => settle(null, new Error('SmartCaptcha network error')));
        widgetIdRef.current = widgetId;
      })
      .catch(() => {
        setRecaptchaErrorCode('RIE001');
      });

    return (): void => {
      const { smartCaptcha } = window as any;
      if (widgetIdRef.current !== null) {
        smartCaptcha?.destroy(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      container.remove();
    };
  }, [isEnabled, settle]);

  // The token is one-time, so every submit resets the widget and mints a new one.
  const getRecaptchaToken = useCallback(async (): Promise<string> => {
    const { smartCaptcha } = window as any;
    const widgetId = widgetIdRef.current;

    if (!smartCaptcha || widgetId === null) {
      setRecaptchaErrorCode('RIE002');
      throw new Error('SmartCaptcha is not loaded');
    }

    settle(null);

    return new Promise<string>((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      smartCaptcha.reset(widgetId);
      smartCaptcha.execute(widgetId);
    });
  }, [settle]);

  return {
    recaptchaErrorCode,
    getRecaptchaToken,
  };
}
